import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { ExpandedListViewItemDefinition } from '../../../core/model/expanded-list-view-item';
import { CompanyService } from '../../../rent/service/data/company.service';
import { LocalDate } from '../../../core/model/local-date';
import { ReportService } from '../../service/data/report.service';
import { RentalObjectCategory } from '../../../rent/model/const/enums';
import { NavigationService } from '../../../core/service/navigation.service';
import { Subscription } from 'rxjs';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { PropertyService } from '../../../rent/service/data/property.service';
import { canSubmitReportForm } from '../../utility/report-form-validation';

@Component({
    selector: 'app-voluntary-tax-liability',
    templateUrl: './voluntary-tax-liability.component.html',
    styleUrls: ['./voluntary-tax-liability.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VoluntaryTaxLiabilityComponent implements OnInit {
  constructor(
    private _cdf: ColumnDefinitionFactory,
    companyService: CompanyService,
    propertyService: PropertyService,
    private _dataService: ReportService,
    private _nativeWindow: NativeWindowService,
    private _navigationService: NavigationService,
    fb: FormBuilder,
    fcf: FormControlExFactory
  ) {
    const today = new Date();
    this.calculationDate = LocalDate.fromDate(today);
    this.companyService = companyService;
    this.propertyService = propertyService;
    this.form = fb.group({
      company: fcf.createMandatory('COMMON.COMPANY', null),
      property: fcf.create('COMMON.PROPERTY', null),
      onlyVoluntaryVat: fcf.create('REPORT.INCLUDE_ARTICLES', 'onlyVoluntaryVat'),
      calculationDate: fb.group({}),
    });
    this.columnDefinitions = [
      this._cdf.create('COMMON.AREA', 'surfaceTotal'),
      this._cdf.create('REPORT.VAT_AREA', 'surfaceVat'),
      this._cdf.create('REPORT.VAT_AREA_PERCENT', 'surfaceVatPercent'),
      this._cdf.create('REPORT.NO_VAT_AREA', 'surfaceNoVat'),
      this._cdf.create('REPORT.NO_VAT_AREA_PERCENT', 'surfaceNoVatPercent'),
    ];
  }

  companyService: CompanyService;
  propertyService: PropertyService;
  columnDefinitions: ColumnDefinition[];
  
  realEstateProperties: { name: string; value: string }[];
  dataContext: ExpandedListViewItemDefinition[];
  form: FormGroup;
  calculationDate: LocalDate;
  onlyVoluntarySelection: { name: string; value: string }[];

  ngOnInit() {
    this.onlyVoluntarySelection = [{ name: 'ARTICLE.VOLUNTARY_VAT', value: 'onlyVoluntaryVat' }];
    this.onlyVoluntarySelection.push({ name: 'REPORT.ALL', value: 'all' });
  }

  ngOnDestroy(): void {
    if (this._subscription) this._subscription.unsubscribe();
  }

  onPrintPages() {
    if (!canSubmitReportForm(this.form)) return;
    window.print();
  }

  exportExcel(): void {
    if (!canSubmitReportForm(this.form)) return;

    if (this._subscription) this._subscription.unsubscribe();

    this._subscription = this._dataService.getVoluntaryTaxLiabilityReportExcel(this.form.value).subscribe(file => {
      this._nativeWindow.save(file);
    });
  }

  private _subscription: Subscription;

  onSubmit() {
    if (!canSubmitReportForm(this.form)) return;

    if (this._subscription) this._subscription.unsubscribe();

    this._subscription = this._dataService.getVoluntaryTaxLiabilityReport(this.form.value).subscribe(data => {
      this.dataContext = data.map(reportData => {
        const model = new ExpandedListViewItemDefinition();
        model.caption = reportData.propertyNumber + ' ' + reportData.municipality + ' ' + reportData.propertyName;
        model.columnDefinitions = [
          this._cdf.create('RENTAL_OBJECT_FORM.RENTAL_OBJECT_NUMBER_LABEL', 'rentalId'),
          this._cdf.create('RENTAL_OBJECT_FORM.RENTAL_OBJECT_TYPE_LABEL', 'rentalObjectTypeName'),
          this._cdf.create('LEASE_AGREEMENT.TENANT_ONE', 'tenantOneName'),
          this._cdf.create('LEASE_AGREEMENT.TENANT_ONE_CIVICNUMBER', 'tenantOneIdString', ColumnVisibility.optional),
          this._cdf.create('COMMON.AREA', 'surface'),
          this._cdf.createDate('REPORT.VAT_FROM', 'vatFrom'),
        ];
        model.dataContext = reportData;
        model.icon = 'account-statement';
        model.itemsSource = reportData.rentalObjects.map(x => {
          return {
            rentalObjectId: x.rentalObjectId,
            rentalObjectCategory: x.rentalObjectCategory,
            rentalId: x.rentalId,
            rentalObjectTypeName: x.rentalObjectTypeName,
            tenantOneName: x.tenantOneName,
            tenantOneIdString: x.tenantOneIdString,
            surface: x.surface,
            vatFrom: x.vatFrom,
          };
        });
        return model;
      });
    });
  }

  private _getPath(category: RentalObjectCategory) {
    let segment = '';
    switch (category) {
      case RentalObjectCategory.Dwelling:
      case RentalObjectCategory.Parking:
      case RentalObjectCategory.Premises:
        segment = RentalObjectCategory[category].toLowerCase();
        break;

      case RentalObjectCategory.Other:
        segment = 'other-rental-object';
        break;

      default:
        throw new Error(`The specified contract category is not supported. Category: ${category}`);
    }
    return segment;
  }

  onExpandedContentRowClicked(e: { context: any; event: any }): void {
    this._navigationService.navigate(
      ['my-property', this._getPath(e.context.rentalObjectCategory), 'edit', e.context.rentalObjectId],
      null,
      e.event.ctrlKey
    );
  }
}
